/**
 * Critère UNIQUE et partagé pour décider si un repli local (écriture directe en
 * table, RPC, fonction `…Legacy()`) est légitime après l'échec d'un appel à une
 * fonction edge.
 *
 * Le problème d'origine (audit 0.7) : les wrappers enveloppaient l'appel dans un
 * `try` et basculaient sur une écriture directe depuis le navigateur dans le
 * `catch`, sans jamais regarder POURQUOI l'appel avait échoué. Résultat, un
 * refus explicite du serveur était traité exactement comme un câble réseau
 * débranché, et le repli franchissait la décision du serveur :
 *  - `subscriptions-management` refuse `sessions_total < 1` ou un second
 *    abonnement actif → le repli insérait quand même la ligne ;
 *  - `courses-management` refuse « Cannot delete course: scheduled classes
 *    exist » → le repli supprimait quand même le cours.
 *
 * Ce n'était pas rattrapable avant : l'ancienne `invokeWithRetry` levait une
 * erreur brute dont le statut HTTP n'était pas lisible (`error.status` toujours
 * `undefined`, le vrai statut étant sur `error.context.status`). Depuis sa
 * réécriture, l'erreur normalisée porte `status`, et `invokeWithRetry` distingue
 * en plus explicitement `authExpired` (401 persistant) et `forbidden` (403).
 *
 * Règle retenue — le repli n'est autorisé QUE si le serveur n'a jamais pu se
 * prononcer :
 *
 * | Cas                                        | `isTransportFailure` | Repli   |
 * | ------------------------------------------ | -------------------- | ------- |
 * | Réseau coupé / DNS / CORS / abort           | `true`               | permis  |
 * | 5xx (fonction indisponible ou plantée)      | `true`               | permis  |
 * | 401 persistant (`authExpired`)              | `false`              | interdit|
 * | 403 (`forbidden`, droits insuffisants)      | `false`              | interdit|
 * | 4xx quelconque (règle métier, validation)   | `false`              | interdit|
 * | erreur métier renvoyée en HTTP 200          | `false`              | interdit|
 *
 * Autrement dit : dès qu'une réponse HTTP a été produite par la fonction edge
 * (statut < 500), c'est une DÉCISION du serveur, et le client n'a pas à la
 * rejouer autrement. En l'absence de statut, aucune réponse n'a été reçue :
 * c'est une panne de transport, et le repli reste le comportement dégradé voulu.
 *
 * Le seuil est placé à 500 plutôt qu'à 400 parce que « fonction indisponible »
 * (502/503/504, ou 500 sur un crash du runtime) est précisément le scénario pour
 * lequel le repli a été écrit.
 */

import { isSessionExpiredError } from './sessionExpired';

/** Sous-ensemble du résultat de `invokeWithRetry` nécessaire à la décision. */
export interface RefusalSignals {
  /** 401 persistant : la déconnexion a déjà été déclenchée par le wrapper. */
  authExpired?: boolean;
  /** 403 : utilisateur authentifié mais sans les droits demandés. */
  forbidden?: boolean;
}

/** Le statut est tantôt sur l'erreur normalisée, tantôt sur son `context`. */
function statusOf(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const candidate = error as { status?: unknown; context?: { status?: unknown } };
  if (typeof candidate.status === 'number') return candidate.status;
  if (typeof candidate.context?.status === 'number') return candidate.context.status;
  return undefined;
}

/**
 * `true` uniquement si l'échec vient du transport et qu'aucune décision serveur
 * n'a été rendue. Tout le reste est un refus : l'erreur doit remonter à
 * l'appelant, jamais déclencher une écriture directe depuis le navigateur.
 */
export function isTransportFailure(error: unknown, signals: RefusalSignals = {}): boolean {
  // Pas d'erreur : il n'y a rien à replier.
  if (!error) return false;

  // Refus explicites signalés par `invokeWithRetry`. On les teste avant le
  // statut car ils sont sans ambiguïté possible.
  if (signals.authExpired || signals.forbidden) return false;

  // Une `SessionExpiredError` est un 401 persistant remonté sous forme d'erreur
  // levée (cf. sessionExpired.ts). Elle ne porte NI statut HTTP NI le drapeau
  // `authExpired` : sans ce test elle tomberait dans la branche
  // « status === undefined » ci-dessous et serait classée panne de transport,
  // donc autoriserait un repli en écriture directe depuis le navigateur — alors
  // qu'il n'y a plus de session et que le serveur a bel et bien répondu 401.
  if (isSessionExpiredError(error)) return false;

  const status = statusOf(error);

  // Aucune réponse HTTP reçue (fetch échoué, DNS, CORS, abort, timeout client) :
  // le serveur n'a pas pu se prononcer, le repli est le comportement dégradé
  // prévu.
  if (status === undefined) return true;

  // La fonction edge a répondu : 4xx (refus de droits, règle métier, validation)
  // et 200-avec-charge-utile-d'erreur sont des décisions du serveur.
  // Seul un 5xx signifie « la fonction n'a pas pu s'exécuter ».
  return status >= 500;
}

/** Inverse lisible d'`isTransportFailure`, pour les tests d'intention. */
export function isServerRefusal(error: unknown, signals: RefusalSignals = {}): boolean {
  return !!error && !isTransportFailure(error, signals);
}

/**
 * `true` si l'échec est un dépassement du délai client (`invokeWithRetry` pose
 * `timedOut: true` sur l'erreur).
 */
export function isTimeoutFailure(error: unknown): boolean {
  return !!error && typeof error === 'object' && (error as { timedOut?: unknown }).timedOut === true;
}

/**
 * Critère de repli pour une ÉCRITURE (insert / update / delete / RPC mutante).
 *
 * Il est plus strict qu'`isTransportFailure` sur un seul point : le timeout.
 *
 * Les deux se présentent identiquement (`status === undefined`, aucune réponse
 * HTTP reçue), mais ils ne disent pas la même chose :
 *
 * - **Réseau coupé / DNS / CORS** : la requête n'est jamais partie, ou la
 *   réponse ne reviendra jamais parce que le lien est mort. Le repli local est
 *   le comportement dégradé voulu, et c'est le comportement historique.
 * - **Timeout** : le lien fonctionne, c'est le SERVEUR qui n'a pas fini. La
 *   fonction edge est très probablement encore en train de s'exécuter. Rejouer
 *   l'écriture depuis le navigateur produit alors deux écritures pour une seule
 *   action de l'utilisateur.
 *
 * Le scénario concret : `createSubscription` dépasse 30 s, la fonction edge
 * finit par insérer la ligne, et pendant ce temps le navigateur insère la
 * sienne. Les gardes rejouées côté client ne rattrapent pas le cas :
 *  - la garde « pas de second abonnement actif » ne cherche que
 *    `status = 'active'`, alors que `useClientCSVImport` crée des abonnements
 *    `pending` — un doublon de `pending` passe ;
 *  - même pour un abonnement `active`, la garde ne voit rien tant que
 *    l'insertion du serveur n'est pas committée ;
 *  - `createCourse` et `createCategory` n'ont AUCUNE garde d'unicité, ni côté
 *    serveur ni côté repli.
 *
 * On ferme donc le repli en écriture sur un timeout : l'erreur remonte, et
 * l'utilisateur constate l'état réel au rafraîchissement suivant plutôt que de
 * se retrouver avec deux lignes. Le bénéfice du timeout est intact : on cesse
 * d'attendre, l'écran ne reste pas bloqué en chargement.
 *
 * À utiliser à la place d'`isTransportFailure` PARTOUT où le repli écrit.
 * `isTransportFailure` reste le bon critère pour un repli en LECTURE : y
 * relire les données après un timeout est sans conséquence.
 */
export function isLocalWriteFallbackAllowed(
  error: unknown,
  signals: RefusalSignals = {}
): boolean {
  if (isTimeoutFailure(error)) return false;
  return isTransportFailure(error, signals);
}
